/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Dispatch, SetStateAction } from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { styled } from "@mui/material/styles";
import MTextField from "@mui/material/TextField";
import Button from "components/atoms/buttons/Button";
import Switch from "components/atoms/Switch";
import { handleAllowWholeNumberAsInput, handlePreventSpecialCharactersInInput } from "utils/HelperFunctions";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateIcon } from "components/atoms/Icon";
import { isReleaseInSchedulingState, isReleaseScheduled } from "utils/helpers";
import { EllipsisVerticalIcon, MegaphoneIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import { DATE_TIME_PICKER_FORMAT } from "../../../../../constants";

const StyledMTextField = styled(MTextField)(() => ({
  "& .MuiInputBase-input": {
    boxShadow: "none",
    fontFamily: "satoshi",
    color: "white",
    fontWeight: "500",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "10px",
    borderColor: "#22222255",
    borderWidth: "1px",
  },

  "& .MuiInput-underline:after": {
    borderBottomColor: "white",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "white",
      textColor: "white",
    },
    "&:hover fieldset": {
      borderColor: "white",
      textColor: "white",
    },
    "&.Mui-focused fieldset": {
      borderColor: "white",
      textColor: "white",
    },
  },
}));

function ScheduleReleaseForm({
  onClose,
  editReleaseId,
  isStartTimeModalOpen,
  isEndTimeModalOpen,
  handleFieldChange,
  data,
  errorState,
  currentlyEditingRelease,
  isEndDateProvided,
  magnetAvailableSupply,
  handleCreateDraftRelease,
  handleUpdateRelease,
  toggleIsEndDateProvided,
  maxEndTimeForRelease,
  setIsStartTimeModalOpen,
  setIsEndTimeModalOpen,
}: {
  onClose: () => void;
  editReleaseId: number | undefined;
  isStartTimeModalOpen: boolean;
  isEndTimeModalOpen: boolean;
  handleFieldChange: (event: any, property?: string, propValue?: any) => void;
  data: ReleaseStateType;
  errorState: any;
  currentlyEditingRelease: any;
  isEndDateProvided: boolean;
  magnetAvailableSupply: number;
  handleCreateDraftRelease: () => void;
  handleUpdateRelease: (editReleaseId: number) => void;
  toggleIsEndDateProvided: (func: (prev: boolean) => boolean) => void;
  maxEndTimeForRelease: moment.Moment;
  setIsStartTimeModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsEndTimeModalOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const handleStartTimePickerChange = (newValue: any) => {
    handleFieldChange(null, "startTime", newValue);
  };

  const handleEndTimePickerChange = (newValue: any) => {
    handleFieldChange(null, "endTime", newValue);
  };

  const handleReleaseSupplyChange = (event: any) => {
    handleFieldChange(null, "supplyForRelease", event.target.value);
  };

  const handleSalePrice = (event: any) => {
    const val = event.target.value;
    handleFieldChange(null, "salePrice", val === "" ? 0 : parseInt(val, 10));
  };

  const handleOnBlur = (event: any) => {
    const { name } = event.target;
    handleFieldChange(null, name, parseInt(data.salePrice.toFixed(2), 10));
  };
  return (
    <>
      <div className="flex flex-col gap-x-6">
        <div className="relative mt-7 flex justify-between gap-6">
          <div className="flex flex-1 flex-col gap-2" onClick={(event) => event.stopPropagation()}>
            <label className="font-sora text-base font-medium xl:text-sm">Start Date</label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              {/* Start time. */}
              <DateTimePicker
                components={{
                  OpenPickerIcon: DateIcon,
                }}
                renderInput={(props) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <StyledMTextField {...props} error={!!errorState.start_time} helperText={errorState.start_time} />
                )}
                label=""
                // disablePast={Boolean(editReleaseId)}
                disablePast
                // minDateTime={new Date()}
                value={data.startTime}
                minDate={maxEndTimeForRelease}
                onChange={handleStartTimePickerChange}
                DialogProps={{ fullScreen: true, fullWidth: true }}
                onOpen={() => {
                  setIsStartTimeModalOpen(true);
                  setIsEndTimeModalOpen(false);
                }}
                onClose={() => setIsStartTimeModalOpen(false)}
                open={isStartTimeModalOpen}
                closeOnSelect
                inputFormat={DATE_TIME_PICKER_FORMAT}
              />
            </LocalizationProvider>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label className="font-sora text-base font-medium xl:text-sm">Supply for Release</label>
            <StyledMTextField
              className="flex-1 font-sans text-lg font-medium xl:text-sm"
              type="number"
              name="supplyForRelease"
              value={data.supplyForRelease}
              onChange={handleReleaseSupplyChange}
              // @ts-ignore
              onKeyDown={handleAllowWholeNumberAsInput}
              label=""
              disabled={
                !!(
                  editReleaseId &&
                  (isReleaseScheduled(currentlyEditingRelease?.status) ||
                    isReleaseInSchedulingState(currentlyEditingRelease?.status))
                )
              }
              error={!!errorState.max_supply}
              helperText={errorState.max_supply}
            />
          </div>
          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
          <i className="absolute -top-0 right-1 text-xs">Max Supply: {magnetAvailableSupply}</i>
        </div>
        <div className="flex justify-between gap-6 sm:mt-5 xl:mt-7">
          <div
            onClick={(event) => event.stopPropagation()}
            className={`${!isEndDateProvided ? "visible" : "invisible"}  flex flex-1 flex-col gap-2`}
          >
            <label className="font-sora text-base font-medium xl:text-sm">End Date</label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                components={{
                  OpenPickerIcon: DateIcon,
                }}
                renderInput={(props) => (
                  <StyledMTextField {...props} error={!!errorState.end_time} helperText={errorState.end_time} />
                )}
                label=""
                disabled={!data.startTime}
                value={data.endTime}
                // minTime={isSameDate ? moment(data.startTime).minute(data.startTime.minutes() + 1) : undefined}
                minDate={moment(data.startTime)}
                onChange={handleEndTimePickerChange}
                onOpen={() => {
                  setIsEndTimeModalOpen(true);
                  setIsStartTimeModalOpen(false);
                }}
                onClose={() => setIsEndTimeModalOpen(false)}
                open={isEndTimeModalOpen}
                closeOnSelect
                inputFormat={DATE_TIME_PICKER_FORMAT}
              />
            </LocalizationProvider>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label className="font-sora text-base font-medium xl:text-sm">Sale Price</label>
            <StyledMTextField
              className="flex-1 font-sans text-lg font-medium xl:text-sm"
              type="number"
              name="salePrice"
              value={data.salePrice}
              onChange={handleSalePrice}
              onKeyDown={(e: any) => handlePreventSpecialCharactersInInput(e, true)}
              onBlur={handleOnBlur}
              label=""
              error={!!errorState.price}
              helperText={errorState.price}
              InputProps={{
                startAdornment: <strong className="pr-1 font-satoshi text-white">$</strong>,
              }}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between sm:mt-5 xl:mt-7">
        <Switch
          labelClasses="font-medium text-lg"
          label="No End Date"
          checked={isEndDateProvided}
          onChange={() => toggleIsEndDateProvided((prev) => !prev)}
        />
      </div>

      {/* Create/Update Release */}
      <div className="mt-3 flex w-full flex-1 items-center justify-end">
        <div className="flex gap-2">
          <Button onClick={onClose} variant="whiteOutline" text="CANCEL" additionalClasses="text-xs" />
          {editReleaseId ? (
            <Button
              onClick={() => handleUpdateRelease(editReleaseId)}
              variant="magnetiqSolid"
              text="UPDATE RELEASE DETAILS"
              additionalClasses="text-xs"
            />
          ) : (
            <span className="flex gap-x-2">
              <Button
                onClick={handleCreateDraftRelease}
                variant="magnetiqSolid"
                text="SAVE DRAFT"
                additionalClasses="text-xs"
              />
            </span>
          )}
        </div>
      </div>
      {editReleaseId ? null : (
        <p className="ml-auto mt-4 w-[90%] text-right text-xxs leading-tight opacity-75">
          To publish your release, please navigate to the Release Table Actions{" "}
          <EllipsisVerticalIcon className="-mt-0.5 mr-1 inline-block h-3.5 w-3.5" />. From there, you can review and
          refine the details before choosing the &lsquo;{" "}
          <MegaphoneIcon className="-mt-1 mr-1.5 inline-block h-3.5 w-3.5" aria-hidden="true" />
          <u>Publish Release</u> &rsquo; option to make the release live.
        </p>
      )}
    </>
  );
}

export default ScheduleReleaseForm;
